import { zodResolver } from "@hookform/resolvers/zod";
import React, { memo, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";

import { sobOfferFormSchema, type SobOfferFormValues } from "../model/schemas";
import { getStationName } from "../model/utils";

import type {
  GetOfferOrderDTO,
  OfferOrderDTOTariffRoad,
  OfferOrderDTOTariffStation,
  OrderDetailResponseDTO,
} from "@/common/api";
import { useGetOfferOrderDetails, useOfferResponse } from "@/common/api";
import {
  Button,
  Form,
  Input,
  LoadingModal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth, useToast } from "@/common/hooks";
import { TARIFF_LABELS } from "@/common/model";
const StyledTableHead = ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
  <TableHead className="text-left p-3 md:p-4" {...props}>
    {children}
  </TableHead>
);

const StyledTableCell = ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
  <TableCell className="text-left p-3 md:p-4" {...props}>
    {children}
  </TableCell>
);

interface SobOfferFormProps {
  order: OrderDetailResponseDTO;
  detail?: GetOfferOrderDTO;
}

export const SobOfferForm = memo(({ order, detail }: SobOfferFormProps) => {
  const { auth } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const form = useForm<SobOfferFormValues>({
    resolver: zodResolver(sobOfferFormSchema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      tariffs: [],
      wagon: undefined,
    },
  });

  const { data: offerDetailsData, isLoading } = useGetOfferOrderDetails(
    {
      userId: auth.id!,
      orderId: order.id!,
    },
    {
      query: {
        enabled: detail === undefined,
      },
    },
  );

  const offerDetails = useMemo(
    () => detail ?? offerDetailsData?.data.payload ?? null,
    [detail, offerDetailsData],
  );

  useEffect(() => {
    if (offerDetails) {
      form.reset({
        tariffs:
          offerDetails.listDirection?.map((item) => ({
            wagonTariff: item.wagonTariff,
            indexNumber: item.indexNumber ?? 0,
            tariffRoad: item.tariffRoad ?? "COEFFICIENT",
          })) ?? [],
        wagon: offerDetails.countWagonsOwner ?? undefined,
      });
    }
  }, [offerDetails, form]);

  const submitOfferMutation = useOfferResponse({
    mutation: {
      onSuccess: () => {
        toast({
          title: "Успешно",
          description: "Предложение отправлено",
        });

        setLocation(ROUTES.ORDERS);
      },
      onError: (error: any) => {
        toast({
          title: "Ошибка",
          description: error?.message ?? "Не удалось отправить предложение",
          variant: "destructive",
        });
      },
    },
  });

  const handleSubmit = (data: SobOfferFormValues) => {
    const offers = data.tariffs?.map((item) => ({
      wagonTariff: item.wagonTariff,
      indexNumber: item.indexNumber,
    }));

    submitOfferMutation.mutate({
      data: {
        orderId: order.id!,
        userId: auth.id!,
        offers,
        countWagonsOwner: data.wagon,
      },
    });
  };

  const getUnitLabel = (
    tariffRoad?: OfferOrderDTOTariffRoad,
    tariffStation?: OfferOrderDTOTariffStation,
  ) =>
    tariffStation ? TARIFF_LABELS[tariffStation] : tariffRoad ? TARIFF_LABELS[tariffRoad] : null;

  if (isLoading && detail === undefined) {
    return <LoadingModal isOpen={true} />;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <div className="mb-5 md:mb-6 overflow-hidden">
          <div className="p-3 md:p-4">
            <h2 className="text-2xl font-semibold text-Text-main leading-7">Предложение</h2>
          </div>

          <Table className="min-w-[600px] md:min-w-full mb-3 md:mb-0">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Дорога/станция выгрузки</StyledTableHead>
                <StyledTableHead>Тариф за вагон</StyledTableHead>
                <StyledTableHead>Тип тарифа</StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offerDetails?.listDirection?.map((item, index) => {
                const unit = getUnitLabel(item.tariffRoad, item.tariffStation);
                return (
                  <TableRow key={item.indexNumber}>
                    <StyledTableCell>
                      {getStationName(item.unloadingRoad, item.unloadingStation) ?? "-"}{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <div className="flex flex-col gap-1">
                        <input
                          type="hidden"
                          {...form.register(`tariffs.${index}.indexNumber`)}
                          value={item.indexNumber ?? 0}
                        />
                        <input
                          type="hidden"
                          {...form.register(`tariffs.${index}.tariffRoad`)}
                          value={item.tariffRoad ?? "COEFFICIENT"}
                        />
                        <Input
                          {...form.register(`tariffs.${index}.wagonTariff`, {
                            onChange: () => {
                              form.trigger(`tariffs.${index}.wagonTariff`);
                            },
                          })}
                          size="sm"
                          type="number"
                          step="any"
                          className={`w-full max-w-[120px] ${
                            form.formState.errors.tariffs?.[index]?.wagonTariff
                              ? "border-destructive"
                              : ""
                          }`}
                          disabled={offerDetails.responseSign}
                        />
                        {form.formState.errors.tariffs?.[index]?.wagonTariff && (
                          <p className="text-xs font-normal leading-4 text-destructive">
                            {form.formState.errors.tariffs[index]?.wagonTariff?.message}
                          </p>
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>{unit ?? "-"}</StyledTableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <StyledTableCell>
                  <b>Количество вагонов</b>
                </StyledTableCell>
                <StyledTableCell>Требуется {offerDetails?.totalWagons}</StyledTableCell>
                <StyledTableCell>
                  <div className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-2">
                      Заявлено
                      <Input
                        {...form.register("wagon")}
                        size="sm"
                        type="number"
                        step="1"
                        className={`max-w-[120px] ${
                          form.formState.errors.wagon ? "border-destructive" : ""
                        }`}
                        disabled={offerDetails?.responseSign}
                      />
                    </span>
                    {form.formState.errors.wagon && (
                      <p className="text-xs font-normal leading-4 text-destructive">
                        {form.formState.errors.wagon.message}
                      </p>
                    )}
                  </div>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={submitOfferMutation.isPending || offerDetails?.responseSign}
            className="bg-Main-orange hover:bg-Main-orange/90 text-white rounded-md px-6 py-2 w-full md:w-auto"
          >
            {submitOfferMutation.isPending ? "Отправка..." : "Отправить предложение"}
          </Button>
        </div>
      </form>
    </Form>
  );
});
